import { useCounter } from '../hooks/useCounter'

// Animated Metric Component
const AnimatedMetric = ({ label, value, icon }: { label: string; value: string; icon: React.ReactNode }) => {
  // Extract numeric value and suffix (handles formats like "150+", "$50M+", "340%", "98%")
  const numericMatch = value.match(/(\d+)(.*)/)
  const numericValue = numericMatch ? parseInt(numericMatch[1]) : 0
  const suffix = numericMatch ? numericMatch[2] : value
  
  const { count, ref } = useCounter({ 
    end: numericValue, 
    duration: 2500,
    startOnView: true 
  })

  return (
    <div ref={ref} className="text-center">
      <div className="flex justify-center mb-4">{icon}</div>
      <div className="text-3xl font-bold text-gray-900 mb-2">
        {count}{suffix}
      </div>
      <div className="text-gray-600">{label}</div>
    </div>
  )
}

const CaseStudies = () => {
  const caseStudies = [
    {
      id: 1,
      title: "Global Manufacturing Company Automates Quality Control",
      company: "TechManufacturing Corp",
      industry: "Manufacturing",
      challenge: "Manual quality control processes were causing production delays and inconsistent results. The company needed to inspect 10,000+ units daily with 99.9% accuracy.",
      solution: "Implemented computer vision AI agents for real-time quality inspection, integrated with existing production lines and automated rejection systems.",
      results: [
        "95% reduction in inspection time",
        "99.95% accuracy rate achieved",
        "$2.3M annual cost savings",
        "Zero manual inspection errors"
      ],
      technologies: ["Computer Vision", "TensorFlow", "Industrial IoT", "Real-time Processing"],
      timeline: "3 months",
      image: "/images/case-study-manufacturing.jpg",
      testimonial: "The AI quality control system transformed our production line. We're now catching defects we would have missed before, and our efficiency has never been higher.",
      author: "Sarah Chen, VP of Operations"
    },
    {
      id: 2,
      title: "Financial Services Firm Automates Compliance Reporting",
      company: "SecureFinance Partners",
      industry: "Financial Services",
      challenge: "Regulatory compliance reporting required 40+ hours weekly from specialized staff, with high risk of human error and missed deadlines.",
      solution: "Developed AI agents for automated data collection, analysis, and compliance report generation with real-time regulatory monitoring.",
      results: [
        "99% reduction in manual work",
        "100% on-time compliance submissions",
        "$500K annual cost savings",
        "Zero compliance violations"
      ],
      technologies: ["Natural Language Processing", "Data Analytics", "Regulatory APIs", "Automated Reporting"],
      timeline: "2 months",
      image: "/images/case-study-finance.jpg",
      testimonial: "What used to take our team two full days now happens automatically in minutes. The accuracy and reliability are outstanding.",
      author: "Michael Rodriguez, Chief Compliance Officer"
    },
    {
      id: 3,
      title: "E-commerce Platform Revolutionizes Customer Service",
      company: "ShopSmart Online",
      industry: "E-commerce",
      challenge: "Customer service team overwhelmed with 15,000+ daily inquiries across multiple languages, leading to long wait times and customer dissatisfaction.",
      solution: "Deployed multilingual AI customer service agents with deep learning capabilities for natural conversation and automated issue resolution.",
      results: [
        "80% of inquiries automated",
        "5-second average response time",
        "92% customer satisfaction rate",
        "60% cost reduction"
      ],
      technologies: ["Large Language Models", "Multilingual NLP", "Sentiment Analysis", "Integration APIs"],
      timeline: "6 weeks",
      image: "/images/case-study-ecommerce.jpg",
      testimonial: "Our customers love the instant, accurate responses. The AI agents handle complex questions better than we expected.",
      author: "Jessica Liu, Head of Customer Experience"
    },
    {
      id: 4,
      title: "Healthcare System Optimizes Patient Flow",
      company: "MediCare Regional Hospital",
      industry: "Healthcare",
      challenge: "Inefficient patient scheduling and resource allocation leading to long wait times, staff burnout, and suboptimal patient outcomes.",
      solution: "Implemented AI-powered patient flow optimization system with predictive analytics for demand forecasting and resource allocation.",
      results: [
        "40% reduction in wait times",
        "30% improvement in bed utilization",
        "25% increase in patient satisfaction",
        "$1.2M annual operational savings"
      ],
      technologies: ["Predictive Analytics", "Machine Learning", "Healthcare APIs", "Real-time Optimization"],
      timeline: "4 months",
      image: "/images/case-study-healthcare.jpg",
      testimonial: "The system has transformed how we manage patient flow. Our staff is less stressed, and patients receive better care.",
      author: "Dr. Amanda Foster, Chief Medical Officer"
    },
    {
      id: 5,
      title: "Logistics Company Optimizes Route Planning",
      company: "FastTrack Logistics",
      industry: "Logistics & Transportation",
      challenge: "Manual route planning for 500+ daily deliveries resulting in fuel waste, delayed deliveries, and customer complaints.",
      solution: "Developed AI-powered route optimization system with real-time traffic analysis, weather integration, and dynamic rerouting capabilities.",
      results: [
        "35% reduction in fuel costs",
        "50% faster delivery times",
        "95% on-time delivery rate",
        "$800K annual savings"
      ],
      technologies: ["Route Optimization", "Real-time Analytics", "GPS Integration", "Machine Learning"],
      timeline: "10 weeks",
      image: "/images/case-study-logistics.jpg",
      testimonial: "Our drivers love the optimized routes, and our customers notice the difference. It's been a game-changer for our business.",
      author: "Robert Kim, Operations Director"
    },
    {
      id: 6,
      title: "Insurance Company Automates Claims Processing",
      company: "ProInsure Solutions",
      industry: "Insurance",
      challenge: "Claims processing taking 2-3 weeks due to manual document review, leading to customer frustration and high operational costs.",
      solution: "Implemented AI document processing system with automated claim validation, fraud detection, and approval workflows.",
      results: [
        "90% faster claim processing",
        "99.7% fraud detection accuracy",
        "85% customer satisfaction improvement",
        "$1.5M annual cost reduction"
      ],
      technologies: ["Document AI", "Fraud Detection", "Computer Vision", "Workflow Automation"],
      timeline: "12 weeks",
      image: "/images/case-study-insurance.jpg",
      testimonial: "Claims that used to take weeks now process in hours. Our customers are thrilled, and our team can focus on complex cases.",
      author: "Maria Santos, Claims Director"
    }
  ]

  const metrics = [
    { 
      label: "Projects Completed", 
      value: "150+", 
      icon: (
        <div className="w-12 h-12 bg-[#212331] rounded-lg flex items-center justify-center">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="white" strokeWidth="2" fill="white"/>
          </svg>
        </div>
      )
    },
    { 
      label: "Average ROI", 
      value: "340%", 
      icon: (
        <div className="w-12 h-12 bg-[#212331] rounded-lg flex items-center justify-center">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <line x1="18" y1="6" x2="6" y2="18" stroke="white" strokeWidth="2"/>
            <polyline points="15,6 18,6 18,9" stroke="white" strokeWidth="2" fill="none"/>
            <polyline points="9,18 6,18 6,15" stroke="white" strokeWidth="2" fill="none"/>
          </svg>
        </div>
      )
    },
    { 
      label: "Cost Savings Generated", 
      value: "$50M+", 
      icon: (
        <div className="w-12 h-12 bg-[#212331] rounded-lg flex items-center justify-center">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <line x1="12" y1="1" x2="12" y2="23" stroke="white" strokeWidth="2"/>
            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" stroke="white" strokeWidth="2" fill="none"/>
          </svg>
        </div>
      )
    },
    { 
      label: "Client Satisfaction", 
      value: "98%", 
      icon: (
        <div className="w-12 h-12 bg-[#212331] rounded-lg flex items-center justify-center">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="white" strokeWidth="2" fill="none"/>
          </svg>
        </div>
      )
    }
  ]

  return (
    <div className="case-studies-page">
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center overflow-hidden bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('/images/testbackgroundoneai5.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-0 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center px-4 py-2 bg-white/95 text-blue-700 rounded-full text-sm font-semibold mb-8 backdrop-blur-sm shadow-lg">
              Case Studies
            </div>
            
            <h1 className="text-5xl lg:text-7xl font-bold text-white leading-tight mb-6 drop-shadow-lg">
              Real Results{' '}
              <span className="block">
                <span className="bg-gradient-to-r from-blue-300 to-purple-300 bg-clip-text text-transparent drop-shadow-lg">
                  Real Impact
                </span>
              </span>
            </h1>
            
            <p className="text-xl lg:text-2xl text-white leading-relaxed mb-8 max-w-3xl mx-auto drop-shadow-md">
              Discover how businesses across industries have transformed their operations 
              and achieved remarkable results with our AI solutions.
            </p>
          </div>
        </div>
      </section>

      {/* Metrics Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {metrics.map((metric, index) => (
              <AnimatedMetric
                key={index}
                label={metric.label}
                value={metric.value}
                icon={metric.icon}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Success{' '}
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Stories
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Detailed case studies showcasing the transformative power of our AI solutions.
            </p>
          </div>

          <div className="space-y-16 max-w-6xl mx-auto">
            {caseStudies.map((study, index) => (
              <div key={study.id} className={`flex flex-col ${index % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-12 items-center`}>
                {/* Content */}
                <div className="flex-1">
                  <div className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">
                    {study.industry}
                  </div>
                  
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">{study.title}</h3>
                  <p className="text-lg text-gray-600 mb-6">{study.company}</p>
                  
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Challenge:</h4>
                    <p className="text-gray-600">{study.challenge}</p>
                  </div>
                  
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Solution:</h4>
                    <p className="text-gray-600">{study.solution}</p>
                  </div>
                  
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Results:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {study.results.map((result, idx) => (
                        <div key={idx} className="flex items-center text-gray-700">
                          <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          {result}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Technologies Used:</h4>
                    <div className="flex flex-wrap gap-2">
                      {study.technologies.map((tech, idx) => (
                        <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="bg-gray-100 p-6 rounded-lg">
                    <blockquote className="text-gray-700 italic mb-4">
                      "{study.testimonial}"
                    </blockquote>
                    <cite className="font-semibold text-gray-900">— {study.author}</cite>
                  </div>
                  
                  <div className="mt-6 flex items-center text-sm text-gray-600">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Project Timeline: {study.timeline}
                  </div>
                </div>
                
                {/* Image */}
                <div className="flex-1">
                  <div className="rounded-2xl h-80 overflow-hidden shadow-lg bg-gray-100">
                    <img 
                      src={study.image} 
                      alt={`${study.company} case study - ${study.title}`}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.parentElement!.innerHTML = '<div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50"><span class="text-gray-500 text-lg">Project Visualization</span></div>';
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Industries{' '}
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                We Serve
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our AI solutions have delivered proven results across diverse industries.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 max-w-4xl mx-auto">
            {[
              {
                name: "Manufacturing",
                icon: (
                  <div className="w-16 h-16 bg-[#212331] rounded-lg flex items-center justify-center">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="4" width="18" height="16" rx="2" stroke="white" strokeWidth="2" fill="none"/>
                      <rect x="6" y="8" width="12" height="2" fill="white"/>
                      <rect x="6" y="12" width="8" height="2" fill="white"/>
                      <rect x="6" y="16" width="10" height="2" fill="white"/>
                      <circle cx="18" cy="6" r="2" fill="white"/>
                    </svg>
                  </div>
                )
              },
              {
                name: "Healthcare",
                icon: (
                  <div className="w-16 h-16 bg-[#212331] rounded-lg flex items-center justify-center">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M22 12h-4l-3 9L9 3l-3 9H2" stroke="white" strokeWidth="2" fill="none"/>
                    </svg>
                  </div>
                )
              },
              {
                name: "Finance",
                icon: (
                  <div className="w-16 h-16 bg-[#212331] rounded-lg flex items-center justify-center">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <line x1="12" y1="1" x2="12" y2="23" stroke="white" strokeWidth="2"/>
                      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" stroke="white" strokeWidth="2" fill="none"/>
                    </svg>
                  </div>
                )
              },
              {
                name: "E-commerce",
                icon: (
                  <div className="w-16 h-16 bg-[#212331] rounded-lg flex items-center justify-center">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="9" cy="21" r="1" stroke="white" strokeWidth="2" fill="white"/>
                      <circle cx="20" cy="21" r="1" stroke="white" strokeWidth="2" fill="white"/>
                      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" stroke="white" strokeWidth="2" fill="none"/>
                    </svg>
                  </div>
                )
              },
              {
                name: "Logistics",
                icon: (
                  <div className="w-16 h-16 bg-[#212331] rounded-lg flex items-center justify-center">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="1" y="3" width="15" height="13" stroke="white" strokeWidth="2" fill="none"/>
                      <polygon points="16,3 21,8 21,16 16,16" stroke="white" strokeWidth="2" fill="none"/>
                      <circle cx="5.5" cy="18.5" r="2.5" stroke="white" strokeWidth="2" fill="none"/>
                      <circle cx="18.5" cy="18.5" r="2.5" stroke="white" strokeWidth="2" fill="none"/>
                    </svg>
                  </div>
                )
              },
              {
                name: "Insurance",
                icon: (
                  <div className="w-16 h-16 bg-[#212331] rounded-lg flex items-center justify-center">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="white" strokeWidth="2" fill="none"/>
                      <polyline points="9,12 10.5,13.5 15,9" stroke="white" strokeWidth="2" fill="none"/>
                    </svg>
                  </div>
                )
              }
            ].map((industry, index) => (
              <div key={index} className="text-center">
                <div className="flex justify-center mx-auto mb-3">
                  {industry.icon}
                </div>
                <span className="text-sm font-medium text-gray-700">{industry.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Ready to Write Your Success Story?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Join the growing list of companies transforming their business with our AI solutions.
            </p>
            <a 
              href="/contact" 
              className="inline-flex items-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              Start Your Transformation
              <svg className="w-5 h-5 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14m-7-7l7 7-7 7" />
              </svg>
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}

export default CaseStudies
